import { useCallback, useEffect, useRef, useState } from "react";

import { ApolloError, ObservableQuery } from "@apollo/client";
import { datadogRum } from "@datadog/browser-rum";
import { isEqual } from "lodash";
import { useNavigate } from "react-router";
import { useShallow } from "zustand/react/shallow";

import {
  CommonListType,
  CreateCheckInput,
  InboundingListProductMetadataInput,
  InputMaybe,
  InventoryChangeReason,
  ListProductStatus,
} from "__graphql__/types";
import {
  CollaborativeInboundProgressedAction,
  CollaborativeInboundStage,
  CollaborativeInboundUpdateState,
  PageName,
} from "analytics/events";
import { useAppLanguageStore } from "app/stores/useAppLanguageStore";
import { routes } from "config/routes";
import { useAnalytics } from "shared/hooks/useAnalytics";
import { useCustomToast } from "shared/hooks/useCustomToast";
import { countryNameFromCode } from "utils/countryNames";
import { getShelfDetail } from "utils/item";
import { isNullOrUndefined } from "utils/tsHelpers";

import {
  ClaimedProductsState,
  DeliveryType,
  InboundingType,
  TranslatedClaimedProduct,
} from "./inboundStore/types";
import { useInboundStore } from "./inboundStore/useInboundStore";
import {
  buildProductPayload,
  createInitialStockUpdate,
  getDisplayUnitSize,
  mapInboundingTypeToDeliveryType,
} from "./inboundStore/utils";
import { useInboundEventTracker } from "../hooks/useInboundEventTracker/useInboundEventTracker";
import {
  GetPartialProductsFromClaimedListQuery,
  GetProductsFromClaimedListQuery,
  useAddProductsToInboundingListMutation,
  useClaimInboundingListMutation,
  useDeleteInboundingListProductsMutation,
  useEditProductsInInboundingListMutation,
  useGetPartialProductsFromClaimedListQuery,
  useGetProductsFromClaimedListLazyQuery,
  useInboundProductMutation,
  useReleaseInboundingListMutation,
  useCreateCheckMutation,
} from "../queries/collaborativeInbound/collaborativeInbound.generated";
import { useGetMultipleDesadvsRolliIdLazyQuery } from "../queries/despatchAdvice/despatchAdvice.generated";

const CLAIM_SHARED_LIST_ERROR_TOAST_ID = "claim_shared_list_error_toast";
const CLAIM_SHARED_LIST_SUCCESS_TOAST_ID = "claim_shared_list_success_toast";
const CLAIM_INBOUND_PRODUCT_ERROR_TOAST_ID = "claim_inbound_product_error_toast";
const GET_CLAIMED_PRODUCTS_ERROR_TOAST_ID = "get_claimed_products_error_toast_id";
const UNCLAIM_LIST_ERROR_TOAST_ID = "unclaim-list-error-toast";
const DELETE_PRODUCT_ERROR_TOAST_ID = "delete-product-error-toast";
const CREATE_CHECK_SUCCESS_TOAST_ID = "create-check-success-toast";
const CREATE_CHECK_ERROR_TOAST_ID = "create-check-error-toast";
const EDIT_PRODUCT_ERROR_TOAST_ID = "edit-product-error-toast";
const LEAST_NUMBER_OF_CLAIMERS = 2;

const RETRY_COUNT = 2;

export function useSerializedClaimedProduct() {
  const appLanguage = useAppLanguageStore((state) => state.appLanguage);

  const transformProductForDisplay = useCallback(
    (
      product: GetProductsFromClaimedListQuery["getProductsFromClaimedList"]["products"][0],
      isQuantityCheckRequired: boolean,
    ): TranslatedClaimedProduct => {
      const shelf = product.inventoryEntry.shelfNumber ?? null;
      const shelfDetail = shelf ? getShelfDetail(shelf) : null;
      const countryOfOriginCode = product.countryOfOrigin?.code?.toUpperCase() ?? null;
      const countryOfOriginName = countryNameFromCode(appLanguage, countryOfOriginCode);
      const handlingUnitSize = product.handlingUnitSize || 1;
      const unitSizeForDisplay = getDisplayUnitSize(product.quantity, handlingUnitSize);
      const stockUpdated =
        product.status === ListProductStatus.list_product_status_inbounded ||
        product.isInbounded === true;
      const totalInboundQuantity = stockUpdated ? 0 : (product.quantity ?? 0);
      const eans: string[] = (product.units ?? [])
        .map((unit) => unit.ean ?? "")
        .filter((ean) => ean !== "");

      return {
        productId: product.id,
        productName: product.name ?? null,
        productSku: product.sku,
        productImageUrl: product.imageUrl ?? null,
        shelf,
        shelfLetter: shelfDetail?.shelfLetter ?? null,
        shelfNumber: shelfDetail?.shelfNumber ?? null,
        numberOfShelfFacings: product.numberOfShelfFacings ?? 0,
        numberOfShelfStackings: product.numberOfShelfStackings ?? null,
        isShelvedInHandlingUnits: !!product.isShelvedInHandlingUnits,
        countryOfOriginName,
        countryOfOriginCode,
        isProductBio: product.bio ?? null,
        unitType: handlingUnitSize > 1 ? "handling" : "single",
        handlingUnitSize,
        unitSizeForDisplay,
        totalInboundQuantity,
        stockOnShelf: product.inventoryEntry.stock?.shelf ?? 0,
        status: product.status,
        eans,
        stockUpdated,
        isQuantityConfirmed: stockUpdated || !isQuantityCheckRequired,
      };
    },
    [appLanguage],
  );

  return transformProductForDisplay;
}

export function useFetchClaimedProducts(isFirstRender = false, shouldCheckDesadvs = false) {
  const navigate = useNavigate();
  const { showToastUI } = useCustomToast();
  const serializeClaimedProduct = useSerializedClaimedProduct();
  const { sendInboundStateUpdate } = useInboundEventTracker();
  const {
    setSelectedOption,
    setCreatorId,
    setOrderId,
    setInboundActiveItem,
    setInboundClaimedProducts,
    setInboundClaimedProductsSubset,
    setInboundUIState,
    setDeliverySSCC,
    updateDespatchAdviceItems,
  } = useInboundStore(
    useShallow((state) => ({
      setSelectedOption: state.setSelectedOption,
      setCreatorId: state.setCreatorId,
      setOrderId: state.setOrderId,
      setInboundActiveItem: state.setInboundActiveItem,
      setInboundClaimedProducts: state.setInboundClaimedProducts,
      setInboundUIState: state.setInboundUIState,
      setInboundClaimedProductsSubset: state.setInboundClaimedProductsSubset,
      setDeliverySSCC: state.setDeliverySSCC,
      updateDespatchAdviceItems: state.updateDespatchAdviceItems,
    })),
  );

  const [isFetchingClaimedProducts, setIsFetchingClaimedProducts] = useState(false);
  const [isFetchingClaimedProductsSubset, setIsFetchingClaimedProductsSubset] = useState(false);
  const [claimerIds, setClaimerIds] = useState<string[]>([]);

  const handleError = useCallback(
    (error: unknown) => {
      setIsFetchingClaimedProducts(false);
      let errorMessage = "Unknown error occurred.";
      if (error instanceof ApolloError) {
        const gqlError = error.graphQLErrors[0];
        const code = gqlError?.extensions?.code;
        const meta = gqlError?.extensions?.meta;
        const listId =
          meta && typeof meta === "object" && "list_id" in meta
            ? (meta as { list_id?: string }).list_id
            : null;
        const message = gqlError?.message;
        if (code === "WRONG_HUB") {
          setInboundUIState({ isUnclaimListModalVisible: listId });
          datadogRum.addError("Wrong hub error while fetching claimed products", {
            errorMessage: message || "WRONG_HUB",
          });
          return;
        }
        if (code === "CLAIMED_LIST_NOT_FOUND") {
          setInboundUIState({
            isProductAlreadyInbounded: false,
            isRefetchClaimedListConfirmationModalVisible: false,
          });
          navigate(routes.inbound.root);
          errorMessage = message || errorMessage;
        }
      } else {
        errorMessage = error instanceof Error ? error.message : errorMessage;
      }
      if (!isFirstRender) {
        showToastUI({
          id: GET_CLAIMED_PRODUCTS_ERROR_TOAST_ID,
          title: "get_claimed_products_error_toast_id.title",
          description: "get_claimed_products_error_toast.description",
        });
        datadogRum.addError("Error while fetching claimed products", { errorMessage });
      }
    },
    [isFirstRender, navigate, setInboundUIState, showToastUI],
  );

  const processClaimedProductsData = (data: GetProductsFromClaimedListQuery) => {
    const { listType, creatorId } = data.getProductsFromClaimedList;
    const isQuantityCheckRequired =
      listType === CommonListType.list_type_regular && creatorId === "system";
    const reason =
      listType === CommonListType.list_type_cancelled_order
        ? InventoryChangeReason.inbound_order_cancellation
        : InventoryChangeReason.inbound_goods_received;
    return data.getProductsFromClaimedList.products.reduce<ClaimedProductsState>(
      (acc, product) => {
        const serializedProduct = serializeClaimedProduct(product, isQuantityCheckRequired);
        const { stockUpdated } = serializedProduct;
        acc.claimedProductsMap[serializedProduct.productSku] = serializedProduct;
        acc.claimedProductsStockUpdates[serializedProduct.productSku] = createInitialStockUpdate(
          reason,
          serializedProduct.totalInboundQuantity,
          stockUpdated,
        );
        acc.claimedProductsSortedByShelf.push({
          name: serializedProduct.productName,
          sku: serializedProduct.productSku,
          shelf: serializedProduct.shelf,
          shelfLetter: serializedProduct.shelfLetter,
          shelfNumber: serializedProduct.shelfNumber,
          eans: serializedProduct.eans,
        });
        return acc;
      },
      {
        claimedProductsMap: {},
        claimedProductsStockUpdates: {},
        claimedProductsSortedByShelf: [],
        droppingListId: data.getProductsFromClaimedList?.id ?? null,
        droppingListName: data.getProductsFromClaimedList?.name ?? null,
        droppingListType: data.getProductsFromClaimedList?.listType,
      },
    );
  };
  const [fetchDesadvs] = useGetMultipleDesadvsRolliIdLazyQuery();
  const handleDespatchAdvice = async (sscc: string | null) => {
    if (isNullOrUndefined(sscc)) {
      return;
    }

    try {
      const { data } = await fetchDesadvs({
        variables: { input: { rolliID: sscc } },
      });

      if (!data) {
        return;
      }

      updateDespatchAdviceItems(data.getMultipleDesadvsRolliID?.despatchAdvices ?? null);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      datadogRum.addError("Error fetching despatch advices:", { errorMessage });
    }
  };

  const handleOnCompleted = (data: GetProductsFromClaimedListQuery) => {
    setIsFetchingClaimedProducts(false);
    if (!data || !data.getProductsFromClaimedList.products.length) {
      showToastUI({
        id: GET_CLAIMED_PRODUCTS_ERROR_TOAST_ID,
        title: "get_claimed_products_error_toast_id.title",
        description: "get_claimed_products_error_toast.description",
      });
      return;
    }
    const claimedProductsData = processClaimedProductsData(data);
    const inboundingType = mapInboundingTypeToDeliveryType(
      data.getProductsFromClaimedList.inboundingType,
    );
    setSelectedOption(inboundingType);
    setCreatorId(data.getProductsFromClaimedList.creatorId ?? null);
    const orderId = data.getProductsFromClaimedList.orderId
      ? data.getProductsFromClaimedList.orderId
      : null;
    const listType = data.getProductsFromClaimedList?.listType ?? null;
    const creatorId = data.getProductsFromClaimedList?.creatorId ?? null;
    setOrderId(orderId);
    setInboundClaimedProducts(claimedProductsData);
    setInboundActiveItem();
    setInboundUIState({
      isRefetchClaimedListConfirmationModalVisible: false,
      isQuantityCheckRequired:
        listType === CommonListType.list_type_regular && creatorId === "system",
    });
    const hasClaimedProducts = claimedProductsData.claimedProductsSortedByShelf.length > 0;
    const sscc = data.getProductsFromClaimedList.sscc ?? null;
    const isCancelledOrder = listType === CommonListType.list_type_cancelled_order;
    setDeliverySSCC(sscc);
    if (isFirstRender && hasClaimedProducts) {
      sendInboundStateUpdate({
        state: CollaborativeInboundUpdateState.DroppingListResumed,
        droppingListId: claimedProductsData.droppingListId,
        sscc,
        inboundingType: isCancelledOrder ? InboundingType.ReinboundedOrder : inboundingType,
        creatorId,
        orderId,
      });
    }
    if (shouldCheckDesadvs) {
      handleDespatchAdvice(sscc);
    }
    navigate(routes.inbound.dropping);
  };

  const [getClaimedProducts] = useGetProductsFromClaimedListLazyQuery({
    fetchPolicy: "no-cache",
    onCompleted: handleOnCompleted,
    onError: handleError,
  });

  const handleOnGetClaimedProductSubsetCompleted = (
    data: GetPartialProductsFromClaimedListQuery,
  ) => {
    setIsFetchingClaimedProductsSubset(false);
    const products = data?.getProductsFromClaimedList?.products ?? [];
    if (products.length) {
      const {
        claimedProductsMap,
        claimedProductsStockUpdates,
        claimedProductsSortedByShelf,
        droppingListName,
        droppingListType,
      } = useInboundStore.getState();
      const hasNew = products.some((product) => {
        const sku = product?.sku;
        return !sku || !(sku in claimedProductsMap);
      });

      if (hasNew) {
        setInboundUIState({ isNewProductsModalVisible: true });
        return;
      }

      const reason =
        droppingListType === CommonListType.list_type_cancelled_order
          ? InventoryChangeReason.inbound_order_cancellation
          : InventoryChangeReason.inbound_goods_received;

      const updatedClaimedProductsData = data.getProductsFromClaimedList.products.reduce<
        Pick<
          ClaimedProductsState,
          "claimedProductsMap" | "claimedProductsStockUpdates" | "droppingListName"
        >
      >(
        (acc, product) => {
          const existingProduct = claimedProductsMap[product.sku];
          const stockUpdated =
            product.status === ListProductStatus.list_product_status_inbounded ||
            product.isInbounded;

          const totalInboundQuantity = stockUpdated ? 0 : (product.quantity ?? 0);
          const handlingUnitSize = product.handlingUnitSize || 1;
          const unitSizeForDisplay = getDisplayUnitSize(product.quantity, handlingUnitSize);

          acc.claimedProductsMap[product.sku] = {
            ...existingProduct,
            totalInboundQuantity,
            unitSizeForDisplay,
            handlingUnitSize,
            stockOnShelf: product.inventoryEntry.stock?.shelf ?? 0,
            isQuantityConfirmed: stockUpdated ? true : existingProduct.isQuantityConfirmed,
          };

          acc.claimedProductsStockUpdates[product.sku] = createInitialStockUpdate(
            reason,
            totalInboundQuantity,
            Boolean(stockUpdated),
          );

          return acc;
        },
        {
          claimedProductsMap: {},
          claimedProductsStockUpdates: {},
          droppingListName: data.getProductsFromClaimedList.name ?? droppingListName,
        },
      );
      const filteredSortedByShelf = claimedProductsSortedByShelf.filter(
        (product) => product.sku in updatedClaimedProductsData.claimedProductsMap,
      );
      const newInboundClaimedProductsSubset = {
        ...updatedClaimedProductsData,
        claimedProductsSortedByShelf: filteredSortedByShelf,
      };

      if (
        !isEqual(newInboundClaimedProductsSubset, {
          claimedProductsMap,
          claimedProductsStockUpdates,
          claimedProductsSortedByShelf,
          droppingListName,
        })
      ) {
        setInboundClaimedProductsSubset(newInboundClaimedProductsSubset);
      }
    }
  };

  const fetchClaimedProducts = useCallback(async () => {
    setIsFetchingClaimedProductsSubset(true);
    await getClaimedProducts();
  }, [getClaimedProducts]);

  const [isPollingStopped, setIsPollingStopped] = useState<boolean>(false);

  const { startPolling, refetch, stopPolling } = useGetPartialProductsFromClaimedListQuery({
    skip: true,
    defaultOptions: {
      fetchPolicy: "network-only",
    },
    pollInterval: 0,
    notifyOnNetworkStatusChange: true,
    onCompleted: (response) => {
      if (
        (response.getProductsFromClaimedList?.claimerIds?.length ?? 0) < LEAST_NUMBER_OF_CLAIMERS
      ) {
        stopPolling();
        setIsPollingStopped(true);
        return;
      }
      setIsPollingStopped(false);
      setClaimerIds(response.getProductsFromClaimedList?.claimerIds ?? []);
      handleOnGetClaimedProductSubsetCompleted(response);
    },
    onError: (error: unknown) => {
      setIsFetchingClaimedProductsSubset(false);
      if (
        error instanceof ApolloError &&
        error?.graphQLErrors?.[0]?.extensions?.code === "CLAIMED_LIST_NOT_FOUND"
      ) {
        setInboundUIState({ isClaimedListNotFoundWarningModalVisible: true });
        return;
      }
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      datadogRum.addError("Error while refreshing claimed products", { errorMessage });
    },
  });

  const refreshClaimedProducts = useCallback(() => {
    refetch().then(() => {
      startPolling(5000);
    });
  }, [startPolling, refetch]);

  return {
    fetchClaimedProducts,
    isFetchingClaimedProducts,
    refreshClaimedProducts,
    isFetchingClaimedProductsSubset,
    isPollingStopped,
    claimerIds,
    handleDespatchAdvice,
  };
}

export function useCheckClaimedProductsOnLanding() {
  const { fetchClaimedProducts, isFetchingClaimedProducts } = useFetchClaimedProducts(true, true);

  useEffect(() => {
    const checkClaimedProducts = async () => {
      await fetchClaimedProducts();
    };
    checkClaimedProducts();
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { isFetchingClaimedProducts };
}

export const useClaimInboundList = (refetchInboundingLists?: ObservableQuery["refetch"]) => {
  const { showToastUI } = useCustomToast();
  const [isClaimingList, setIsClaimingList] = useState(false);
  const { fetchClaimedProducts } = useFetchClaimedProducts(false, true);
  const { sendInboundStateUpdate } = useInboundEventTracker();
  const setSharedListId = useInboundStore((state) => state.setSharedListId);
  const handleError = useCallback(
    async (error: unknown) => {
      setIsClaimingList(false);
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      datadogRum.addError("Claim Shared List Error", { errorMessage });
      if (
        error instanceof ApolloError &&
        error.graphQLErrors?.[0]?.extensions?.code === "ALREADY_HAS_CLAIMED_LIST"
      ) {
        showToastUI({
          id: CLAIM_SHARED_LIST_ERROR_TOAST_ID,
          title: "claim_shared_list_cannot_claim_error_toast",
        });
        await fetchClaimedProducts();
        return;
      }
      showToastUI({
        id: CLAIM_SHARED_LIST_ERROR_TOAST_ID,
        title: "claim_shared_list_error_toast",
      });
      if (refetchInboundingLists) {
        refetchInboundingLists();
      }
    },
    [fetchClaimedProducts, refetchInboundingLists, showToastUI],
  );

  const [claimListMutation] = useClaimInboundingListMutation({
    onError: handleError,
  });

  const claimInboundList = useCallback(
    async ({
      listId,
      deliverySSCC,
      deliveryType,
      creatorId,
      orderId,
      isOrderCanceled,
    }: {
      listId: string;
      deliverySSCC: string | null;
      deliveryType: DeliveryType | null;
      creatorId: string | null;
      orderId: string | null;
      isOrderCanceled?: boolean;
    }) => {
      setIsClaimingList(true);
      setSharedListId(listId);
      const { data } = await claimListMutation({
        variables: { input: { listId } },
      });
      if (data?.claimInboundingList?.success) {
        setIsClaimingList(false);
        showToastUI({
          status: "success",
          id: CLAIM_SHARED_LIST_SUCCESS_TOAST_ID,
          title: "claim_shared_list_success_toast",
        });
        sendInboundStateUpdate({
          state: CollaborativeInboundUpdateState.DroppingListStarted,
          droppingListId: listId,
          sscc: deliverySSCC,
          inboundingType: isOrderCanceled ? InboundingType.ReinboundedOrder : deliveryType,
          creatorId,
          orderId,
        });
        await fetchClaimedProducts();
      }
    },
    [claimListMutation, fetchClaimedProducts, sendInboundStateUpdate, setSharedListId, showToastUI],
  );

  return { claimInboundList, isClaimingList };
};

export const useUnClaimInboundList = (refetchInboundingLists?: ObservableQuery["refetch"]) => {
  const { sendInboundStateUpdate } = useInboundEventTracker();
  const [isUnClaimingList, setIsUnClaimingList] = useState(false);
  const navigate = useNavigate();
  const { showToastUI } = useCustomToast();
  const { resetToInitialState, setInboundUIState } = useInboundStore(
    useShallow((state) => ({
      resetToInitialState: state.resetToInitialState,
      setInboundUIState: state.setInboundUIState,
    })),
  );

  const handleError = useCallback(
    async (error: unknown) => {
      setIsUnClaimingList(false);
      if (error instanceof ApolloError) {
        const graphQLError = error.graphQLErrors?.[0];
        const isListNotClaimed =
          graphQLError?.message === "the inbounding list is not claimed: invalid request";
        const isListNotFound =
          graphQLError?.extensions?.code === "RELEASE_INBOUNDING_LIST_BAD_REQUEST_ERROR";
        if (isListNotClaimed || isListNotFound) {
          setInboundUIState({
            isUnclaimListConfirmationModalVisible: false,
          });
          if (refetchInboundingLists) {
            await refetchInboundingLists();
            setInboundUIState({ isUnclaimListModalVisible: null });
          } else {
            navigate(routes.inbound.root);
          }
          return;
        }
      }
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      datadogRum.addError("UnClaim List Error", { errorMessage });
      showToastUI({
        id: UNCLAIM_LIST_ERROR_TOAST_ID,
        title: "unclaim-list-error-toast",
      });
    },
    [navigate, refetchInboundingLists, setInboundUIState, showToastUI],
  );

  const [releaseInboundingListMutation] = useReleaseInboundingListMutation({
    onError: handleError,
  });

  const unClaimInboundList = useCallback(
    async ({
      listId,
      deliverySSCC,
      deliveryType,
      creatorId,
      orderId,
      droppingListType,
    }: {
      listId: string;
      deliverySSCC: string | null;
      deliveryType: DeliveryType | null;
      creatorId: string | null;
      orderId: string | null;
      droppingListType?: string | null;
    }) => {
      setIsUnClaimingList(true);
      const { data } = await releaseInboundingListMutation({
        variables: { input: { listId } },
      });
      if (data?.releaseInboundingList?.success) {
        const isCancelledOrder = droppingListType === CommonListType.list_type_cancelled_order;
        setIsUnClaimingList(false);
        sendInboundStateUpdate({
          state: CollaborativeInboundUpdateState.DroppingListUnclaimed,
          droppingListId: listId,
          sscc: deliverySSCC,
          inboundingType: isCancelledOrder ? InboundingType.ReinboundedOrder : deliveryType,
          isManualUnclaim: true,
          creatorId,
          orderId,
        });
        if (refetchInboundingLists) {
          await refetchInboundingLists();
          setInboundUIState({ isUnclaimListModalVisible: null });
        } else {
          resetToInitialState();
          setInboundUIState({
            isUnclaimListConfirmationModalVisible: false,
          });
          navigate(routes.inbound.root);
        }
      }
    },
    [
      navigate,
      refetchInboundingLists,
      releaseInboundingListMutation,
      resetToInitialState,
      sendInboundStateUpdate,
      setInboundUIState,
    ],
  );

  return { unClaimInboundList, isUnClaimingList };
};

export const useInboundClaimedProduct = () => {
  const { sendSegmentTrackEvent } = useAnalytics();
  const { showToastUI } = useCustomToast();
  const {
    droppingListId,
    droppingListName,
    orderId,
    claimedProductsMap,
    claimedProductsStockUpdates,
    updateStockForInboundReasons,
    setInboundActiveItem,
    setInboundUIState,
  } = useInboundStore(
    useShallow((state) => ({
      droppingListId: state.droppingListId,
      droppingListName: state.droppingListName,
      orderId: state.orderId,
      claimedProductsMap: state.claimedProductsMap,
      claimedProductsStockUpdates: state.claimedProductsStockUpdates,
      inboundUIState: state.inboundUIState,
      updateStockForInboundReasons: state.updateStockForInboundReasons,
      setInboundActiveItem: state.setInboundActiveItem,
      setInboundUIState: state.setInboundUIState,
    })),
  );
  const [isInboundingProduct, setIsInboundingProduct] = useState(false);
  const retryCountRef = useRef(0);

  const handleError = useCallback(
    (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      datadogRum.addError("Claim Inbound Product Error", { errorMessage });
      showToastUI({
        id: CLAIM_INBOUND_PRODUCT_ERROR_TOAST_ID,
        title: "inbound_product_claim_error_toast.title",
        description: "inbound_product_claim_error_toast.description",
      });
    },
    [showToastUI],
  );

  const [inboundClaimedProductMutation] = useInboundProductMutation({
    fetchPolicy: "network-only",
  });

  const handleInboundClaimedProduct = useCallback(
    async (sku: string, productId: string, droppingListType: CommonListType) => {
      try {
        await inboundClaimedProductMutation({
          variables: { input: { productId } },
        });
        updateStockForInboundReasons(sku);
        setInboundActiveItem(sku);
        const inventoryChangeReason =
          droppingListType === CommonListType.list_type_cancelled_order
            ? InventoryChangeReason.inbound_order_cancellation
            : InventoryChangeReason.inbound_goods_received;
        const quantityInbounded =
          claimedProductsStockUpdates[sku]?.stockUpdatePlan.find(
            ({ reason }) => reason === inventoryChangeReason,
          )?.quantityDelta ?? 0;
        const { handlingUnitSize, shelf } = claimedProductsMap[sku];
        sendSegmentTrackEvent("inboundProgressed", {
          action: CollaborativeInboundProgressedAction.ProductDropped,
          quantity: quantityInbounded,
          dropping_list_id: droppingListId!,
          product_sku: sku,
          is_handling_unit: handlingUnitSize > 1,
          sscc: null,
          shelf_number: shelf,
          dropping_list_name: droppingListName,
          inbounding_stage: CollaborativeInboundStage.ListDropping,
          reason: null,
          order_id: orderId,
        });
      } catch (error) {
        if (
          error instanceof ApolloError &&
          error?.graphQLErrors?.[0]?.message ===
            "Cannot inbound a product that is not claimed by you.: creator ID mismatch"
        ) {
          setInboundUIState({ isUnclaimedListConfirmationModalVisible: true });
        } else if (
          error instanceof ApolloError &&
          error?.graphQLErrors?.[0]?.message ===
            "Product is already inbounded.: product has already been inbounded"
        ) {
          setInboundUIState({
            isProductAlreadyInbounded: false,
            isRefetchClaimedListConfirmationModalVisible: true,
          });
        } else if (retryCountRef.current < RETRY_COUNT) {
          retryCountRef.current += 1;
          await handleInboundClaimedProduct(sku, productId, droppingListType);
        } else {
          const errorMessage = error instanceof Error ? error.message : "Unknown error";
          datadogRum.addError("Claim Inbound Product Error", { errorMessage });
          showToastUI({
            id: CLAIM_INBOUND_PRODUCT_ERROR_TOAST_ID,
            title: "inbound_product_claim_error_toast.title",
            description: "inbound_product_claim_error_toast.description",
          });
          retryCountRef.current = 0;
          setInboundUIState({
            isProductAlreadyInbounded: false,
            isRefetchClaimedListConfirmationModalVisible: true,
          });
        }
        sendSegmentTrackEvent("errorShown", {
          screen_name: PageName.INBOUND_DROPPING_PAGE,
          component_value: "inbounding_request_failed",
        });
      }
    },
    [
      claimedProductsMap,
      claimedProductsStockUpdates,
      droppingListId,
      droppingListName,
      inboundClaimedProductMutation,
      orderId,
      sendSegmentTrackEvent,
      setInboundActiveItem,
      setInboundUIState,
      showToastUI,
      updateStockForInboundReasons,
    ],
  );

  const inboundClaimedProduct = useCallback(
    async (sku: string, droppingListType: CommonListType, productId?: string) => {
      setIsInboundingProduct(true);
      try {
        if (!productId) {
          handleError(new Error("Missing product ID"));
          return;
        }
        if (!claimedProductsStockUpdates[sku]) {
          handleError(new Error("Missing stock update plan"));
          return;
        }
        const { unitSizeForDisplay, totalInboundQuantity } = claimedProductsMap[sku];
        if (!unitSizeForDisplay) {
          handleError(new Error("Missing display state"));
          return;
        }
        if (totalInboundQuantity === 0) {
          handleError(new Error("Stock updates must contain an inbounding"));
          return;
        }
        await handleInboundClaimedProduct(sku, productId, droppingListType);
      } finally {
        // Reset on every path so a failed tap doesn't leave the card ignoring further taps
        setIsInboundingProduct(false);
      }
    },
    [claimedProductsMap, claimedProductsStockUpdates, handleError, handleInboundClaimedProduct],
  );

  return { inboundClaimedProduct, isInboundingProduct };
};

export const useDeleteClaimedProduct = () => {
  const { sendSegmentTrackEvent } = useAnalytics();
  const [isDeletingProduct, setIsDeletingProduct] = useState(false);
  const { showToastUI } = useCustomToast();
  const removeInboundUnitFromDroppingList = useInboundStore(
    (state) => state.removeInboundUnitFromDroppingList,
  );
  const { droppingListName, claimedProductsStockUpdates, claimedProductsMap, setInboundUIState } =
    useInboundStore(
      useShallow((state) => ({
        droppingListName: state.droppingListName,
        claimedProductsStockUpdates: state.claimedProductsStockUpdates,
        claimedProductsMap: state.claimedProductsMap,
        setInboundUIState: state.setInboundUIState,
      })),
    );
  const handleError = useCallback(
    (error: unknown) => {
      setIsDeletingProduct(false);
      if (
        error instanceof ApolloError &&
        error?.graphQLErrors?.[0]?.message ===
          "the request can not be performed for this hub_code or creator_id: invalid request"
      ) {
        setInboundUIState({ isUnclaimedListConfirmationModalVisible: true });
        return;
      }
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      datadogRum.addError("Delete Product Error", { errorMessage });
      showToastUI({
        id: DELETE_PRODUCT_ERROR_TOAST_ID,
        title: "delete-product-error-toast",
      });
    },
    [setInboundUIState, showToastUI],
  );

  const [deleteInboundingListProducts] = useDeleteInboundingListProductsMutation();

  const deleteClaimedProduct = useCallback(
    async (listId: string, sku: string) => {
      try {
        setIsDeletingProduct(true);
        await deleteInboundingListProducts({
          variables: { input: { listId, skus: [sku] } },
        });
        const quantityInbounded =
          claimedProductsStockUpdates[sku]?.stockUpdatePlan.find(
            ({ reason }) => reason === InventoryChangeReason.inbound_goods_received,
          )?.quantityDelta ?? 0;
        sendSegmentTrackEvent("inboundProgressed", {
          action: CollaborativeInboundProgressedAction.ProductRemovedFromList,
          quantity: quantityInbounded,
          dropping_list_id: listId,
          product_sku: sku,
          is_handling_unit: claimedProductsMap[sku].handlingUnitSize > 1,
          sscc: null,
          shelf_number: claimedProductsMap[sku].shelf,
          dropping_list_name: droppingListName,
          inbounding_stage: CollaborativeInboundStage.ListDropping,
        });
        removeInboundUnitFromDroppingList(sku);
      } catch (e: any) {
        handleError(e);
      } finally {
        setIsDeletingProduct(false);
      }
    },
    [
      claimedProductsMap,
      claimedProductsStockUpdates,
      deleteInboundingListProducts,
      droppingListName,
      handleError,
      removeInboundUnitFromDroppingList,
      sendSegmentTrackEvent,
    ],
  );

  return { deleteClaimedProduct, isDeletingProduct };
};

export const useCreateCheck = () => {
  const [isCreatingCheck, setIsCreatingCheck] = useState(false);
  const { showToastUI } = useCustomToast();
  const handleError = useCallback(
    (error: unknown) => {
      setIsCreatingCheck(false);
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      datadogRum.addError("Request Stock Check Error", { errorMessage });
      showToastUI({
        id: CREATE_CHECK_ERROR_TOAST_ID,
        title: "request-stock-check-error-toast",
        description: errorMessage,
      });
    },
    [showToastUI],
  );

  const [createCheck] = useCreateCheckMutation();

  const createStockCheck = useCallback(
    async ({ metadataReason, priority, shelfNumber, sku, type }: CreateCheckInput) => {
      try {
        setIsCreatingCheck(true);
        await createCheck({
          variables: {
            input: {
              metadataReason,
              priority,
              shelfNumber,
              sku,
              type,
            },
          },
        });
        showToastUI({
          id: CREATE_CHECK_SUCCESS_TOAST_ID,
          title: "request-stock-check-success-toast",
          status: "success",
        });
      } catch (e: any) {
        handleError(e);
      } finally {
        setIsCreatingCheck(false);
      }
    },
    [createCheck, handleError, showToastUI],
  );

  return { isCreatingCheck, createStockCheck };
};

export const useEditClaimedProduct = () => {
  const { fetchClaimedProducts } = useFetchClaimedProducts();
  const { sendSegmentTrackEvent } = useAnalytics();
  const [isEditingProduct, setIsEditingProduct] = useState(false);
  const { showToastUI } = useCustomToast();
  const updateInboundQuantityForDisplay = useInboundStore(
    (state) => state.updateInboundQuantityForDisplay,
  );
  const handleError = useCallback(
    (error: unknown) => {
      setIsEditingProduct(false);
      const errorMessage = error instanceof Error ? error.message : "Unknown error";
      datadogRum.addError("Edit Product Error", { errorMessage });
      showToastUI({
        id: EDIT_PRODUCT_ERROR_TOAST_ID,
        title: "edit-product-error-toast",
        description: errorMessage,
      });
      fetchClaimedProducts();
    },
    [fetchClaimedProducts, showToastUI],
  );

  const [editInboundProducts] = useEditProductsInInboundingListMutation();
  const editInboundProduct = useCallback(
    async ({
      droppingListId,
      droppingListName,
      sku,
      shelf,
      newValue,
      oldValue,
    }: {
      droppingListId: string;
      droppingListName: string;
      sku: string;
      shelf: string | null;
      newValue: number;
      oldValue: number;
    }) => {
      try {
        setIsEditingProduct(true);
        await editInboundProducts({
          variables: {
            input: {
              listId: droppingListId,
              products: [
                {
                  sku,
                  quantity: newValue,
                  handlingUnitSize: 1,
                },
              ],
            },
          },
        });
        updateInboundQuantityForDisplay(sku, newValue);
        sendSegmentTrackEvent("inboundProgressed", {
          action: CollaborativeInboundProgressedAction.ProductUpdatedQuantity,
          quantity: newValue - oldValue,
          dropping_list_id: droppingListId,
          product_sku: sku,
          is_handling_unit: false,
          sscc: null,
          shelf_number: shelf,
          dropping_list_name: droppingListName,
          inbounding_stage: CollaborativeInboundStage.ListDropping,
        });
      } catch (e: any) {
        handleError(e);
      } finally {
        setIsEditingProduct(false);
      }
    },
    [editInboundProducts, handleError, sendSegmentTrackEvent, updateInboundQuantityForDisplay],
  );

  return { editInboundProduct, isEditingProduct };
};

export const useAddItem = () => {
  const [isAddingProduct, setIsAddingProduct] = useState(false);
  const [addItemsToPreDroppingListMutation] = useAddProductsToInboundingListMutation();
  const { fetchClaimedProducts } = useFetchClaimedProducts();
  const { sendSegmentTrackEvent } = useAnalytics();
  const { showToastUI } = useCustomToast();
  const navigate = useNavigate();
  const { setInboundUIState, setInboundActiveItem } = useInboundStore(
    useShallow((state) => ({
      setInboundUIState: state.setInboundUIState,
      setInboundActiveItem: state.setInboundActiveItem,
    })),
  );

  const addItemToDroppingList = useCallback(
    async ({
      listId,
      product,
    }: {
      listId: string;
      product: {
        sku: string;
        quantity: number;
        handlingUnitSize: number;
        metadata?: InputMaybe<InboundingListProductMetadataInput>;
      };
    }) => {
      setIsAddingProduct(true);
      try {
        await addItemsToPreDroppingListMutation({
          variables: {
            input: {
              listId,
              products: [product],
            },
          },
        });

        sendSegmentTrackEvent("inboundProgressed", {
          dropping_list_id: listId,
          product_sku: product.sku,
          action: CollaborativeInboundProgressedAction.ProductAddedToList,
          is_handling_unit: product.handlingUnitSize !== 1,
          quantity: product.quantity,
          inbounding_stage: CollaborativeInboundStage.ListDropping,
        });

        await fetchClaimedProducts();
        setInboundUIState({ isClaimedQuantityEditActiveForSku: product.sku });
        setInboundActiveItem(product.sku, true);
      } catch (error) {
        showToastUI({
          status: "error",
          id: "add_items_dropping_list_error_toast",
          title: "add_items_shared_list_error_toast",
        });
        const errorMessage = error instanceof Error ? error.message : "Unknown error";
        datadogRum.addError("Add Item Dropping List Error", { errorMessage });
      } finally {
        setIsAddingProduct(false);
        navigate(routes.inbound.dropping);
      }
    },
    [
      addItemsToPreDroppingListMutation,
      fetchClaimedProducts,
      navigate,
      sendSegmentTrackEvent,
      setInboundActiveItem,
      setInboundUIState,
      showToastUI,
    ],
  );

  return { addItemToDroppingList, isAddingProduct };
};

type ProductSelectionInput = {
  product?: {
    productSku: string;
    quantity?: number;
    handlingUnitSize?: number;
  } | null;
  resetTextSearch: () => void;
};

export const useHandleProductSelection = () => {
  const {
    droppingListId,
    deliverySSCC,
    despatchAdviceItems,
    claimedProductsStockUpdates,
    setInboundActiveItem,
    setInboundUIState,
  } = useInboundStore(
    useShallow((state) => ({
      droppingListId: state.droppingListId,
      deliverySSCC: state.deliverySSCC,
      despatchAdviceItems: state.despatchAdviceItems,
      claimedProductsStockUpdates: state.claimedProductsStockUpdates,
      setInboundActiveItem: state.setInboundActiveItem,
      setInboundUIState: state.setInboundUIState,
    })),
  );

  const { addItemToDroppingList } = useAddItem();
  const navigate = useNavigate();

  const handleProductSelection = useCallback(
    async ({ product, resetTextSearch }: ProductSelectionInput) => {
      if (!product) return;

      const { productSku } = product;
      const foundProduct = claimedProductsStockUpdates[productSku];

      if (foundProduct) {
        const isAlreadyInbounded = !!foundProduct.stockUpdated;
        setInboundActiveItem(productSku, true);
        setInboundUIState(
          isAlreadyInbounded
            ? { isProductAlreadyInbounded: true }
            : { isClaimedQuantityEditActiveForSku: productSku },
        );
        resetTextSearch();
        navigate(routes.inbound.dropping);
        return;
      }

      if (!droppingListId) return;

      const productPayload = buildProductPayload(product, despatchAdviceItems, deliverySSCC);
      await addItemToDroppingList({
        listId: droppingListId,
        product: productPayload,
      });

      resetTextSearch();
    },
    [
      addItemToDroppingList,
      claimedProductsStockUpdates,
      deliverySSCC,
      despatchAdviceItems,
      droppingListId,
      navigate,
      setInboundActiveItem,
      setInboundUIState,
    ],
  );

  return { handleProductSelection };
};
